import { useEffect, useMemo, type Dispatch, type SetStateAction } from 'react';

import useMonthlyMenuFrames from 'common/hook/useMonthlyMenuFrames';
import {
  findFrame,
  resolveMonthKey,
  resolveWeek,
  toWeekOptions,
} from 'common/utils/schoolYearMonths';

/** Phần bộ lọc mà hook này quản: tháng là `month_key` (`YYYY-MM`), tuần là số thứ tự trong khung. */
export interface MonthWeekFilter {
  month?: string;
  week?: number | '';
}

/**
 * Nối ô "Tháng" (và "Tuần ăn") của các màn Bản đồ thực đơn vào khung thực đơn của năm học.
 *
 * Trước đây mỗi màn tự sinh 12 tháng của năm dương lịch rồi ghép `searchStr` với `dayjs().year()`.
 * Năm học trải hai năm dương lịch nên chọn tháng 01 mà gửi năm hiện tại là tra nhầm năm — sai âm
 * thầm. Nay tháng lấy từ khung MenuPlan và mang theo cả năm.
 *
 * Hook tự chọn sẵn khung mới nhất, và tự sửa tuần khi tháng vừa chọn có ít tuần hơn tháng cũ.
 * Dùng bản cập nhật hàm (`setFilter(prev => ...)`) vì hai hiệu ứng dưới đây có thể cùng chạy trong
 * một lượt commit — spread từ biến `filter` của render cũ sẽ nuốt mất thay đổi của hiệu ứng kia.
 */
const useSchoolYearMonthFilter = <T extends MonthWeekFilter>(
  filter: T,
  setFilter: Dispatch<SetStateAction<T>>
) => {
  const { frames, monthOptions, defaultMonthKey, isError, isLoading, schoolYear, abridgedWeekTitles } =
    useMonthlyMenuFrames();

  const frame = useMemo(() => findFrame(frames, filter.month), [frames, filter.month]);
  const weekOptions = useMemo(() => toWeekOptions(frame), [frame]);

  useEffect(() => {
    const next = resolveMonthKey(filter.month, monthOptions, defaultMonthKey);
    if (next === filter.month) return;
    setFilter((prev) => ({ ...prev, month: next }));
  }, [filter.month, monthOptions, defaultMonthKey, setFilter]);

  useEffect(() => {
    const next = resolveWeek(filter.week, weekOptions, !!filter.month);
    if (next === filter.week) return;
    setFilter((prev) => ({ ...prev, week: next }));
  }, [filter.week, filter.month, weekOptions, setFilter]);

  return {
    monthOptions,
    weekOptions,
    /** Khung của tháng đang chọn — cần khi phải biết ngày đầu/cuối thật của khung. */
    frame,
    /** Năm học N (T9/N → T8/N+1). */
    schoolYear,
    /** Không tải được khung (MenuPlan lỗi) — khác với "tháng này chưa chốt khung". */
    framesFailed: isError,
    isLoadingMonths: isLoading,
    /** operation_code → month | year, từ Áp dụng hạn mức trên MenuPlan. */
    abridgedWeekTitles,
  };
};

export default useSchoolYearMonthFilter;
